// docs/34 — the arithmetic of the audio engine, kept pure so it is tested without an AudioContext:
// levels to gains, a local variant pick, the beat offsets inside a playback clip, stereo placement.
import type { SfxBank } from 'virtual:fk-sfx';

/** Decibels to a linear gain. */
export const dbToGain = (db: number): number => 10 ** (db / 20);

/** A 0–100 settings slider to a linear gain on a gentle curve (50 ≈ −12 dB, 0 = silence). */
export function sliderToGain(v: number): number {
  const x = Math.max(0, Math.min(100, v)) / 100;
  return x <= 0 ? 0 : x * x;
}

/** The linear gain of one variant of a bank as mixed, before any per-play multiplier. */
export function variantGain(bank: SfxBank, variant: number): number {
  return dbToGain(bank.gainDb + (bank.variants[variant]?.levelDb ?? 0));
}

/** `[start, count]` of band `band` (wrapped to the bank's bands), or the whole bank. */
export function bandRange(bank: SfxBank, band?: number): [number, number] {
  const n = bank.bands.length;
  if (band === undefined || n === 0) return [0, bank.variants.length];
  const b = bank.bands[((band % n) + n) % n]!;
  return [b.start, b.count];
}

/**
 * A local (unsynced) variant pick: uniform in the band, never the same as `last` when the band has a
 * choice. Server cues never come through here — they carry their variant.
 */
export function pickVariant(bank: SfxBank, rand: () => number, band?: number, last?: number): number {
  const [start, count] = bandRange(bank, band);
  if (count <= 1) return start;
  let v = start + Math.floor(rand() * count);
  if (v === last) v = start + ((v - start + 1 + Math.floor(rand() * (count - 1))) % count);
  return Math.min(start + count - 1, v);
}

/** Uniform in a basis-point band, as a ratio (`[9700, 10300]` → 0.97–1.03). */
export function inBand([lo, hi]: readonly [number, number], rand: () => number): number {
  return (lo + (hi - lo) * rand()) / 10_000;
}

/** Wire codes of `SfxCueRow` (crates/fk-world/src/sound.rs). */
export const ANCHOR = { entry: 0, afterPlayback: 1, now: 2, firstTurn: 3 } as const;
export const BEAT = { start: 0, impact: 1, step: 2, end: 3 } as const;

/**
 * Where the beats of one playback clip fall, in ms from its start at the playing speed: the blow
 * lands 40 % into a lunge and 60 % into a projectile, burst or flash (the moments the target
 * flashes in `EffectPlayer`); a walk's steps start where each square's slide starts.
 */
export interface ClipBeats { impactMs: number; endMs: number; stepMs: number[] }

export function clipBeats(kind: string, ms: number, stepCosts?: readonly number[]): ClipBeats {
  const impact = kind === 'lunge' ? 0.4 : kind === 'projectile' || kind === 'burst' || kind === 'flash' ? 0.6 : 0;
  const stepMs: number[] = [];
  if (stepCosts && stepCosts.length) {
    const total = stepCosts.reduce((a, c) => a + Math.max(1, c), 0);
    let at = 0;
    for (const c of stepCosts) { stepMs.push(at); at += (ms * Math.max(1, c)) / total; }
  }
  return { impactMs: ms * impact, endMs: ms, stepMs };
}

/** Offset of a cue inside its clip (ms at the playing speed), or undefined for a step past the walk. */
export function beatOffset(beats: ClipBeats, beat: number, step: number): number | undefined {
  switch (beat) {
    case BEAT.start: return 0;
    case BEAT.impact: return beats.impactMs;
    case BEAT.end: return beats.endMs;
    case BEAT.step: return beats.stepMs[step];
    default: return 0;
  }
}

/** Stereo placement of a square on the board: the edges sit at ±`width`, the middle in the centre. */
export function panForTile(x: number, boardW: number, width = 0.55): number {
  if (boardW <= 1) return 0;
  const p = ((x + 0.5) / boardW) * 2 - 1;
  return Math.max(-width, Math.min(width, p * width));
}

/** Deterministic 32-bit mix of a string (FNV-1a), for picks every client must agree on without a server row. */
export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/** A seeded uniform source in [0, 1) (mulberry32), for the shared ambience schedule. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
